import React, { useState, useEffect } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  makeStyles,
  Typography,
  InputBase,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { systemAPI } from '../../../../../apis';
import { AutoCompleteSelect } from '../../../../../components';
import { CloseIcon } from '../../../../../components/Icon';
import { i18n } from '../../../../../language';
import { checkSubmitData } from '../../../../../utils/check';
import { classNames } from '../../../../../utils';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiPaper-root': {
      padding: '0',
      margin: '0',
      boxShadow: 'unset',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '43.75rem',
    borderRadius: '0.625rem',
    boxShadow: '0 -2px 21px 0 rgba(30, 56, 140, 0.18)',
    backgroundColor: '#fff',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2.375rem 1.875rem 1.563rem 1.563rem',
  },
  headerTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  closeIcon: {
    fontSize: '1.375rem',
    color: '#538fff',
    margin: '-12px -12px 0 0',
  },
  label: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#7d86a9',
    marginBottom: '0.625rem',
  },
  main: {
    padding: '0.4rem 1.563rem',
    overflow: 'auto',
  },
  InputBase: {
    height: '3.5rem',
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    width: '100%',
    paddingLeft: '1.25rem',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
    },
  },
  cancelBtn: {
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.625rem',
    border: 'solid 1.5px #538fff',
    color: '#538fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    background: '#f5faff',
  },
  configBtn: {
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.625rem',
    background: '#538fff',
    color: '#fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
  },
  checkBox: {
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  moduleAvailabilty: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  labelError: {
    color: '#f44336',
  },
  inputError: {
    border: '1px solid #f44336',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
}));

export default function Ward({ closePop, open, title, confirmClick, confirmText, allData }) {
  const classes = useStyles();
  const loginInfo = useSelector((state) => state.loginInfo);
  const rouList = useSelector((state) => state.rouList);
  // input
  const [inputValues, setInputValues] = useState({
    wardName: allData?.wardName ?? '',
    hospitalId: allData?.hospitalId ?? loginInfo.hospitalId ?? -1,
    wardId: allData?.wardId ?? -1,
    bedStatusYn: allData?.bedStatusYn ?? 0,
    dutyAssignYn: allData?.dutyAssignYn ?? 0,
    ehandOverYn: allData?.ehandOverYn ?? 0,
    equipmentYn: allData?.equipmentYn ?? 0,
    televisitYn: allData?.televisitYn ?? 0,
    admissionYn: allData?.admissionYn ?? 0,
    patSurveyYn: allData?.patSurveyYn ?? 0,
  });

  const [errorMsg, setErrorMsg] = useState(null);
  const schema = {
    wardName: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_WARD_NAME') })),
    hospitalId: Yup.number()
      .min(0, i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_HOSPITAL') }))
      .required(),
  };

  const confirmHandle = async () => {
    try {
      await checkSubmitData(Yup.object().shape(schema), inputValues);
      setErrorMsg(null);
      if (typeof confirmClick === 'function') {
        confirmClick(inputValues);
      }
    } catch (errorMsg) {
      setErrorMsg(errorMsg);
    }
  };

  const inputChange = (name) => {
    return (event) => {
      setInputValues({ ...inputValues, [name]: event.target.value });
    };
  };
  const selectChange = (name) => {
    return (value) => {
      setInputValues({ ...inputValues, [name]: value });
    };
  };

  const checkChange = (name) => {
    return (event) => {
      setInputValues({ ...inputValues, [name]: Number(event.target.checked) });
    };
  };

  // hospital
  const [hospitalWardList, setHospitalWardList] = useState(null);
  const getSysHospitalWardList = async () => {
    const { result } = await systemAPI.getSysWardDropList({
      corpId: loginInfo.corpID,
      hospId: loginInfo.hospitalId,
      userId: loginInfo.userId,
    });
    setHospitalWardList(result);
    return result;
  };

  const hoMapping = React.useMemo(() => {
    if (Array.isArray(hospitalWardList) && hospitalWardList.length > 0) {
      return _.map(hospitalWardList, (item, index) => {
        return { value: item.hospitalId, text: item.hospitalName };
      });
    }
  }, [hospitalWardList, rouList]);

  useEffect(() => {
    (async () => {
      const result = await getSysHospitalWardList();
      if (inputValues.hospitalId === -1) {
        setInputValues({ ...inputValues, hospitalId: result?.[0]?.hospitalId ?? -1 });
      }
    })();
  }, []);

  const getCheckLableClasses = (key) => {
    return errorMsg?.[key] ? classNames(classes.label, classes.labelError) : classes.label;
  };

  const getCheckInputClasses = (key) => {
    return errorMsg?.[key] ? classNames(classes.InputBase, classes.inputError) : classes.InputBase;
  };

  const focusCheck = (name) => {
    return async () => {
      try {
        await checkSubmitData(Yup.object().shape({ [name]: schema[name] }), { [name]: inputValues[name] });
        setErrorMsg({ ...errorMsg, [name]: null });
      } catch (e) {
        setErrorMsg({ ...errorMsg, ...e });
      }
    };
  };

  return (
    <Dialog open={open} maxWidth={false} className={classes.dialog}>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{title}</Typography>
          <IconButton id="setting_ward_pop_clonse" className={classes.closeIcon} onClick={closePop}>
            <CloseIcon />
          </IconButton>
        </div>
        <main className={classes.main}>
          <Box mb="1.875rem">
            <Typography className={getCheckLableClasses('wardName')}>{i18n.t('SETTING_WARD_NAME')}*</Typography>
            <InputBase
              id="setting_ward_pop_input1"
              className={getCheckInputClasses('wardName')}
              placeholder={i18n.t('SETTING_WARD_NAME')}
              onChange={inputChange('wardName')}
              value={inputValues.wardName}
              onBlur={() => {
                (async () => {
                  await focusCheck('wardName')();
                })();
              }}
              inputProps={{ maxLength: 25 }}
            />
            {errorMsg?.wardName ? <Typography className={classes.errorMsg}>{errorMsg.wardName}</Typography> : ''}
          </Box>
          <Typography className={getCheckLableClasses('hospitalId')}>{i18n.t('SETTING_HOSPITAL')}</Typography>
          <AutoCompleteSelect
            id="setting_ward_pop_select1"
            placeholder={i18n.t('SETTING_SELECT')}
            value={inputValues.hospitalId}
            itemList={hoMapping}
            onChange={selectChange('hospitalId')}
            error={Boolean(errorMsg?.hospitalId)}
            errorMsg={errorMsg?.hospitalId}
            onBlur={focusCheck('hospitalId')}
          />
          <Typography className={classes.moduleAvailabilty} style={{ margin: '3.125rem 0 1.25rem 0' }}>
            {i18n.t('SETTING_MODULE_AVAILABILITY')}
          </Typography>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox1"
              control={<Checkbox />}
              label={i18n.t('SETTING_EHANDOVER')}
              className={classes.checkBox}
              checked={Boolean(inputValues.ehandOverYn)}
              onChange={checkChange('ehandOverYn')}
            />
          </Box>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox2"
              control={<Checkbox />}
              label={i18n.t('SETTING_BED_STATUS')}
              className={classes.checkBox}
              checked={Boolean(inputValues.bedStatusYn)}
              onChange={checkChange('bedStatusYn')}
            />
          </Box>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox3"
              control={<Checkbox />}
              label={i18n.t('SETTING_DUTY_ASSIGN')}
              className={classes.checkBox}
              checked={Boolean(inputValues.dutyAssignYn)}
              onChange={checkChange('dutyAssignYn')}
            />
          </Box>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox4"
              control={<Checkbox />}
              label={i18n.t('SETTING_WARD_EQUIPMENT')}
              className={classes.checkBox}
              checked={Boolean(inputValues.equipmentYn)}
              onChange={checkChange('equipmentYn')}
            />
          </Box>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox5"
              control={<Checkbox />}
              label={i18n.t('TELEVISIT_ADD_TELEVISIT')}
              className={classes.checkBox}
              checked={Boolean(inputValues.televisitYn)}
              onChange={checkChange('televisitYn')}
            />
          </Box>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox6"
              control={<Checkbox />}
              label="Admission"
              className={classes.checkBox}
              checked={Boolean(inputValues.admissionYn)}
              onChange={checkChange('admissionYn')}
            />
          </Box>
          <Box display="flex">
            <FormControlLabel
              id="setting_ward_pop_checkbox7"
              control={<Checkbox />}
              label="Appreciation"
              className={classes.checkBox}
              checked={Boolean(inputValues.patSurveyYn)}
              onChange={checkChange('patSurveyYn')}
            />
          </Box>
        </main>
        <Box display="flex" justifyContent="center" style={{ margin: '1.25rem 0 1.875rem 0' }}>
          <ButtonBase
            id="setting_ward_pop_btn1"
            className={classes.cancelBtn}
            style={{ marginRight: '1.313rem' }}
            onClick={closePop}
          >
            {i18n.t('SETTING_CANCEL')}
          </ButtonBase>
          <ButtonBase
            id="setting_ward_pop_btn2"
            className={classes.configBtn}
            onClick={() => {
              (async () => {
                await confirmHandle();
              })();
            }}
          >
            {confirmText}
          </ButtonBase>
        </Box>
      </div>
    </Dialog>
  );
}
